<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="online-appointment-message-panel__header">
      <div class="white--text font-weight-bold">
        {{ templateConfig.title }}
      </div>
    </v-expansion-panel-header>

    <v-expansion-panel-content>
      <div class="pt-5">
        <v-alert
          text
          dense
          color="warning"
          icon="mdi-check-circle-outline"
          class="mb-4"
        >
          <div class="d-flex flex-wrap align-center">
            <div class="font-weight-bold black--text mr-4">
              目前模式：{{ modeText }}
            </div>
            <v-spacer />
            <v-btn
              small
              depressed
              color="white"
              :disabled="saving"
              @click="$emit('switch-mode', templateConfig.key)"
            >
              {{ isCustomMode ? '切換為公版' : '客製化訊息' }}
            </v-btn>
          </div>
        </v-alert>

        <div class="text-body-2 grey--text text--darken-1">
          可插入的項目
        </div>
        <div class="mt-2 d-flex flex-wrap">
          <v-chip
            v-for="variable in variables"
            :key="variable.value"
            small
            color="teal lighten-5"
            text-color="primary"
            class="mr-2 mb-2"
            @click="insertToken(variable.value)"
          >
            <v-icon left x-small>mdi-plus</v-icon>
            {{ variable.label }}
            <v-tooltip bottom max-width="360">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ variable.description }}</span>
            </v-tooltip>
          </v-chip>
        </div>

        <div class="mt-3 text-body-2 font-weight-medium">
          編輯訊息內容（{{ templateConfig.requiredHint }}）
        </div>
        <message-token-editor
          ref="contentInput"
          :value="draftContent"
          :disabled="!isCustomMode"
          :error-messages="inputErrorMessages"
          :variables="variables"
          class="mt-2"
          @input="handleContentInput"
          @disabled-click="handleEditorClick"
        />

        <div class="mt-2 d-flex align-center flex-wrap">
          <v-btn text small color="primary" class="px-0" @click="previewOpen = !previewOpen">
            <v-icon small left>{{ previewOpen ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            {{ previewOpen ? '收合預覽' : '預覽訊息' }}
          </v-btn>
          <v-spacer />
          <div class="text-caption grey--text text--darken-1">
            目前 {{ characterCount }} 字 / {{ smsSegments }} 封
          </div>
        </div>

        <v-expand-transition>
          <v-sheet v-show="previewOpen" rounded color="grey lighten-5" class="mt-3 pa-4">
            <div v-if="showPreviewHeader" class="red-6--text font-weight-bold mb-2">
              {{ clinicName }}通知：
            </div>
            <div class="black--text online-appointment-message-panel__preview">
              {{ previewContent }}
            </div>
          </v-sheet>
        </v-expand-transition>

        <v-alert
          v-if="templateState.successMessage"
          dense
          text
          type="success"
          class="mt-4 mb-0"
        >
          {{ templateState.successMessage }}
        </v-alert>

        <v-card-actions class="px-0 pt-5 pb-0">
          <v-spacer />
          <v-btn text color="grey-5" :disabled="saving" @click="$emit('cancel', templateConfig.key)">
            取消
          </v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            :disabled="!templateState.isDirty || hasLengthError"
            @click="$emit('save', templateConfig.key)"
          >
            儲存
          </v-btn>
        </v-card-actions>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import {
  calculateOnlineAppointmentSmsSegments,
  getOnlineAppointmentTokenLabel,
  MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
  ONLINE_APPOINTMENT_MESSAGE_MODES,
  ONLINE_APPOINTMENT_TEMPLATE_KEYS,
  renderOnlineAppointmentMessagePreview
} from "@/constants/onlineAppointmentMessages";
import MessageTokenEditor from "./MessageTokenEditor.vue";

export default {
  name: "OnlineAppointmentMessageTemplatePanel",

  components: {
    MessageTokenEditor,
  },

  props: {
    templateConfig: {
      type: Object,
      required: true,
    },
    templateState: {
      type: Object,
      required: true,
    },
    clinicName: {
      type: String,
      default: '',
    },
    maxCharacters: {
      type: Number,
      default: MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      previewOpen: false,
    }
  },

  computed: {
    isCustomMode() {
      return this.templateState.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM
    },
    modeText() {
      return this.isCustomMode ? '客製化' : '公版'
    },
    showPreviewHeader() {
      return ![
        ONLINE_APPOINTMENT_TEMPLATE_KEYS.CONFIRMED,
        ONLINE_APPOINTMENT_TEMPLATE_KEYS.FAILED,
      ].includes(this.templateConfig.key)
    },
    draftContent() {
      if (this.isCustomMode) return this.templateState.content || ''
      return this.templateState.defaultContent || this.templateConfig.defaultContent || ''
    },
    variables() {
      return (this.templateState.supportedTokens || [])
        .map(token => ({
          label: getOnlineAppointmentTokenLabel(token),
          value: token,
          description: this.getTokenDescription(token),
        }))
    },
    previewContent() {
      if (!this.templateState.isDirty && this.templateState.backendPreview) {
        return this.templateState.backendPreview
      }

      return renderOnlineAppointmentMessagePreview(this.draftContent, this.clinicName)
    },
    characterCount() {
      if (!this.templateState.isDirty && this.templateState.backendCharacterCount) {
        return Number(this.templateState.backendCharacterCount)
      }

      return this.previewContent.length
    },
    smsSegments() {
      if (!this.templateState.isDirty && this.templateState.backendSmsSegments) {
        return Number(this.templateState.backendSmsSegments)
      }

      return calculateOnlineAppointmentSmsSegments(this.characterCount)
    },
    hasLengthError() {
      return this.characterCount > this.maxCharacters
    },
    inputErrorMessages() {
      const messages = []
      if (this.templateState.contentError) messages.push(this.templateState.contentError)
      if (this.hasLengthError) messages.push(`訊息內容最多 ${this.maxCharacters} 字`)
      return messages
    },
  },

  methods: {
    insertToken(token) {
      if (!this.isCustomMode) {
        this.$emit('editor-click', this.templateConfig.key)
        return
      }

      this.$refs.contentInput?.insertToken(token)
    },
    handleContentInput(content) {
      this.$emit('update-content', this.templateConfig.key, content)
    },
    handleEditorClick() {
      if (!this.isCustomMode) this.$emit('editor-click', this.templateConfig.key)
    },
    getTokenDescription(token) {
      if (token === '#clinic_name#') {
        return '發送時會自動替換成您的診所名稱。依 NCC 規範，商業簡訊必須標示發送者（診所名稱）才能順利送達，可移動位置但無法刪除。'
      }

      if (token === '#appointment_url#') {
        return '發送時會自動替換成患者專屬的預約連結，患者點擊後可查看自己的預約狀況'
      }

      const descriptions = {
        '#patient_name#': '發送時會自動替換成預約患者的姓名。',
        '#appointment_time#': '發送時會自動替換成患者預約的時間。',
        '#clinic_phone#': '發送時會自動替換成診所電話。',
      }

      return descriptions[token] || `發送時會自動替換成「${getOnlineAppointmentTokenLabel(token)}」的實際內容。`
    },
  },
}
</script>

<style scoped>
.online-appointment-message-panel__header {
  background-color: #6d6b66;
  min-height: 44px;
}

.online-appointment-message-panel__preview {
  white-space: pre-line;
}
</style>
